import { FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  Menu,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { PlaceNode } from '@ulysses-inc/harami_api_client'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePlaceLoadingStatus } from 'src/exShared/hooks/usePlaceLoadingStatus'
import {
  initializePlaceFilter,
  splitAccessiblePlaceNode,
} from 'src/exShared/libraries/filterDropDown/place'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import {
  flattenNodes,
  mergedPlaceNodeIds,
} from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import employeesOperations from 'src/state/ducks/employees/operations'
import { GetEmployeesFilter } from 'src/state/ducks/employees/types'
import { RootState } from 'src/state/store'

const EmployeesFilterDropDown = () => {
  const dispatch = useDispatch()

  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const [isIncludeNonAffiliated, setIsIncludeNonAffiliated] =
    useState<boolean>(false)
  const { filters, setListFilter } = useListFilter()
  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()
  const placeLoadingStatus = usePlaceLoadingStatus()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  const {
    employees: { request },
  } = useSelector((state: RootState) => state.employeesState)
  const {
    places: { places },
    placeGroups: { nodes },
  } = useSelector((state: RootState) => state.placesState)

  const placeNodes = getUniqueFlattenNodes([
    ...flattenNodes(nodes ?? []),
    ...(places ?? []),
  ])

  useEffect(() => {
    const loginPlaceUUIDs = splitLoginPlaceUUIDs()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setPlaceNodeIds(loginPlaceUUIDs.loginPlaceIds)
    setPlaceGroupNodeIds(loginPlaceUUIDs.loginPlaceGroupIds)
  }, [])

  useEffect(() => {
    if (placeLoadingStatus !== 'finish') {
      return
    }
    // MEMO: 以下の条件に合致する動作がないので、不要かも知れません。
    if (!filters || !filters.employees) {
      const placeFilter = initializePlaceFilter(placeNodes, true)
      if (placeFilter === undefined || placeFilter.$in.length === 0) {
        // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
        getFilterEmployees()
        return
      }
      const filter = { placeNodeId: placeFilter, isIncludeNonAffiliated: false }
      getFilterEmployees(filter)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setPlaceNodeIds(loginPlaceIds)
      setPlaceGroupNodeIds(loginPlaceGroupIds)
      return
    }
    const employeeFilter = filters.employees.filter
    let filterPlaceNodeIds: string[] = []
    let filterPlaceGroupNodeIds: string[] = []
    if (
      (employeeFilter?.placeNodeIds ?? []).length > 0 ||
      (employeeFilter?.placeGroupNodeIds ?? []).length > 0
    ) {
      filterPlaceNodeIds = employeeFilter?.placeNodeIds ?? []
      filterPlaceGroupNodeIds = employeeFilter?.placeGroupNodeIds ?? []
    } else {
      filterPlaceNodeIds = loginPlaceIds
      filterPlaceGroupNodeIds = loginPlaceGroupIds
    }
    setPlaceNodeIds(filterPlaceNodeIds)
    setPlaceGroupNodeIds(filterPlaceGroupNodeIds)
    setIsIncludeNonAffiliated(employeeFilter?.isIncludeNonAffiliated ?? false)
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    const filter = getFilter(
      filterPlaceNodeIds,
      filterPlaceGroupNodeIds,
      employeeFilter?.isIncludeNonAffiliated,
    )
    getFilterEmployees(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeNodes.length, placeLoadingStatus])

  const getFilterEmployees = (filter?: GetEmployeesFilter) => {
    const limit = Object.keys(request).length ? request.limit : 25
    employeesOperations.getEmployees(
      dispatch,
      { limit: limit, offset: 0 },
      filter,
    )
  }
  const isFilterOn = () => {
    return (
      placeGroupNodeIds.length > 0 ||
      placeNodeIds.length > 0 ||
      isIncludeNonAffiliated
    )
  }

  const clearFilter = () => {
    const filter = getFilter(loginPlaceIds, loginPlaceGroupIds, false)
    getFilterEmployees(filter)
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    setIsIncludeNonAffiliated(false)
    saveFilter()
  }

  const saveFilter = (
    placeNodeIds?: string[],
    placeGroupNodeIds?: string[],
    isIncludeNonAffiliated?: boolean,
  ) => {
    setListFilter({
      ...(filters ?? {}),
      employees: {
        pagination: request,
        filter: {
          placeNodeIds: placeNodeIds,
          placeGroupNodeIds: placeGroupNodeIds,
          isIncludeNonAffiliated: isIncludeNonAffiliated ?? false,
        },
      },
    })
  }

  const getFilter = (
    placeNodeIds: string[],
    placeGroupNodeIds?: string[],
    isIncludeNonAffiliated?: boolean,
  ): GetEmployeesFilter => {
    const allPlaceNodeIds = mergedPlaceNodeIds(
      placeNodes,
      placeNodeIds ?? [],
      placeGroupNodeIds ?? [],
      true,
    )
    const placeNodeIdCondition =
      allPlaceNodeIds && allPlaceNodeIds.length > 0
        ? { $in: allPlaceNodeIds }
        : undefined
    saveFilter(placeNodeIds, placeGroupNodeIds, isIncludeNonAffiliated)
    return {
      placeNodeId: placeNodeIdCondition,
      isIncludeNonAffiliated: isIncludeNonAffiliated ?? false,
    }
  }

  const accessiblePlaceNode = splitAccessiblePlaceNode(placeNodes, places)

  const hoverArea = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clearFilter}>クリア</Button>
      </Stack>
      <Stack>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          現場グループ名
        </Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaceGroups}
          getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
          value={placeGroupNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaceGroups.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const selectedPlaceGroupIds = newValue.map(node => node.uuid)
            setPlaceGroupNodeIds(selectedPlaceGroupIds || [])
            const filterPlaceNodeIds =
              placeNodeIds.length > 0 ||
              (selectedPlaceGroupIds ?? []).length > 0
                ? placeNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              placeNodeIds.length > 0 ||
              (selectedPlaceGroupIds ?? []).length > 0
                ? selectedPlaceGroupIds
                : loginPlaceGroupIds
            const filter = getFilter(
              filterPlaceNodeIds,
              filterPlaceGroupNodeIds ?? [],
              isIncludeNonAffiliated,
            )
            getFilterEmployees(filter)
          }}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox size="small" checked={selected} />
                {option.placeGroup?.name}
              </Stack>
            </li>
          )}
          slotProps={{
            chip: {
              size: 'small',
            },
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          現場名
        </Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaces}
          getOptionLabel={option => option.place?.name ?? '名称未設定'}
          value={placeNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaces.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const selectedPlaceNodeIds = newValue.map(node => node.uuid)
            setPlaceNodeIds(selectedPlaceNodeIds || [])
            const filterPlaceNodeIds =
              (selectedPlaceNodeIds ?? []).length > 0 ||
              placeGroupNodeIds.length > 0
                ? selectedPlaceNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              (selectedPlaceNodeIds ?? []).length > 0 ||
              placeGroupNodeIds.length > 0
                ? placeGroupNodeIds
                : loginPlaceGroupIds
            const filter = getFilter(
              filterPlaceNodeIds || [],
              filterPlaceGroupNodeIds,
              isIncludeNonAffiliated,
            )
            getFilterEmployees(filter)
          }}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.place?.name}
              </Stack>
            </li>
          )}
          slotProps={{
            chip: {
              size: 'small',
            },
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          未所属を含む
        </Typography>
        <Switch
          checked={isIncludeNonAffiliated}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setIsIncludeNonAffiliated(event.target.checked)
            const filterPlaceNodeIds =
              placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                ? placeNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                ? placeGroupNodeIds
                : loginPlaceGroupIds
            const filter = getFilter(
              filterPlaceNodeIds,
              filterPlaceGroupNodeIds || [],
              event.target.checked,
            )
            getFilterEmployees(filter)
          }}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      <Button
        color={isFilterOn() ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
      </Button>
      <Menu
        id="employee-filter-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
          list: {
            sx: {
              padding: 0,
            },
          },
        }}
      >
        {hoverArea}
      </Menu>
    </Box>
  )
}

export default EmployeesFilterDropDown
